import { useMemo, useState } from 'react';
import { X, Search } from 'lucide-react';
import {
  useAdminStores,
  useRescanAnomalies,
  type RescanInput,
  type StoreSummary,
} from '../../hooks/useAnomalies';

interface Props {
  open: boolean;
  onClose: () => void;
}

type Mode = 'single' | 'list' | 'all';
type DateMode = 'yesterday' | 'single' | 'range';

function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

export function ScanDialog({ open, onClose }: Props) {
  const [mode, setMode] = useState<Mode>('single');
  const [singleId, setSingleId] = useState<number | undefined>(undefined);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const [dateMode, setDateMode] = useState<DateMode>('yesterday');
  const [singleDate, setSingleDate] = useState<string>(() => isoDaysAgo(1));
  const [fromDate, setFromDate] = useState<string>(() => isoDaysAgo(7));
  const [toDate, setToDate] = useState<string>(() => isoDaysAgo(1));
  const { data: stores, isLoading: storesLoading } = useAdminStores();
  const rescan = useRescanAnomalies();

  const filtered = useMemo(() => {
    if (!stores) return [];
    const q = search.trim().toLowerCase();
    if (!q) return stores;
    return stores.filter((s) => s.name.toLowerCase().includes(q) || String(s.id).includes(q));
  }, [stores, search]);

  if (!open) return null;

  function close() {
    setSearch('');
    setSingleId(undefined);
    setSelectedIds(new Set());
    setMode('single');
    setDateMode('yesterday');
    rescan.reset();
    onClose();
  }

  function dateScope(): { date?: string; from?: string; to?: string } {
    if (dateMode === 'yesterday') return {};
    if (dateMode === 'single') return { date: singleDate };
    return { from: fromDate, to: toDate };
  }

  function rangeDayCount(): number {
    if (dateMode !== 'range') return 1;
    const a = new Date(fromDate + 'T00:00:00Z').getTime();
    const b = new Date(toDate + 'T00:00:00Z').getTime();
    if (Number.isNaN(a) || Number.isNaN(b) || b < a) return 0;
    return Math.floor((b - a) / 86_400_000) + 1;
  }

  function dateInvalid(): string | null {
    if (dateMode !== 'range') return null;
    if (toDate < fromDate) return '`To` must be on or after `From`';
    if (rangeDayCount() > 90) return 'Maximum 90 days per scan';
    return null;
  }

  async function submit() {
    const scope = dateScope();
    let input: RescanInput;
    if (mode === 'single') {
      if (!singleId) return;
      input = { mode: 'single', storeId: singleId, ...scope };
    } else if (mode === 'list') {
      if (selectedIds.size === 0) return;
      input = { mode: 'list', storeIds: [...selectedIds], ...scope };
    } else {
      input = { mode: 'all', ...scope };
    }
    await rescan.mutateAsync(input);
  }

  function toggleStore(id: number) {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={close}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-800">
          <h3 className="text-sm font-semibold">Run anomaly scan</h3>
          <button onClick={close} className="text-gray-500 hover:text-gray-900">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 flex flex-col gap-4 overflow-hidden">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Stores</div>
            <div className="flex gap-2 text-xs">
              {(['single', 'list', 'all'] as Mode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`px-3 py-1.5 rounded ${
                    mode === m
                      ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                      : 'border border-gray-200 dark:border-gray-800 text-gray-500'
                  }`}
                >
                  {m === 'single'
                    ? 'Single store'
                    : m === 'list'
                      ? 'Multiple stores'
                      : 'All stores'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Dates</div>
            <div className="flex gap-2 text-xs mb-2">
              {(['yesterday', 'single', 'range'] as DateMode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => setDateMode(m)}
                  className={`px-3 py-1.5 rounded ${
                    dateMode === m
                      ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                      : 'border border-gray-200 dark:border-gray-800 text-gray-500'
                  }`}
                >
                  {m === 'yesterday' ? 'Yesterday' : m === 'single' ? 'Single day' : 'Date range'}
                </button>
              ))}
            </div>
            {dateMode === 'single' && (
              <input
                type="date"
                value={singleDate}
                onChange={(e) => setSingleDate(e.target.value)}
                className="text-xs border border-gray-200 dark:border-gray-800 rounded px-2 py-1.5"
              />
            )}
            {dateMode === 'range' && (
              <div className="flex gap-2 items-center text-xs">
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="border border-gray-200 dark:border-gray-800 rounded px-2 py-1.5"
                />
                <span className="text-gray-400">→</span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="border border-gray-200 dark:border-gray-800 rounded px-2 py-1.5"
                />
                <span className="text-gray-500">
                  ({rangeDayCount()} day{rangeDayCount() === 1 ? '' : 's'})
                </span>
              </div>
            )}
            {dateInvalid() && <div className="text-[11px] text-red-600 mt-1">{dateInvalid()}</div>}
          </div>

          {mode !== 'all' && (
            <div className="relative">
              <Search className="absolute left-2 top-2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or id"
                className="text-xs border border-gray-200 dark:border-gray-800 rounded pl-7 pr-2 py-1.5 w-full"
              />
            </div>
          )}

          {mode === 'single' && (
            <div className="border border-gray-200 dark:border-gray-800 rounded overflow-y-auto max-h-72">
              {storesLoading ? (
                <div className="p-3 text-xs text-gray-500">Loading stores…</div>
              ) : (
                filtered.map((s: StoreSummary) => (
                  <button
                    key={s.id}
                    onClick={() => setSingleId(s.id)}
                    className={`w-full text-left text-xs px-3 py-1.5 border-b last:border-b-0 border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 ${
                      singleId === s.id ? 'bg-blue-50 dark:bg-blue-950 text-blue-700' : ''
                    }`}
                  >
                    <span className="font-medium">{s.name}</span>{' '}
                    <span className="text-gray-400">#{s.id}</span>
                  </button>
                ))
              )}
            </div>
          )}

          {mode === 'list' && (
            <div className="border border-gray-200 dark:border-gray-800 rounded overflow-y-auto max-h-72">
              {storesLoading ? (
                <div className="p-3 text-xs text-gray-500">Loading stores…</div>
              ) : (
                filtered.map((s: StoreSummary) => {
                  const checked = selectedIds.has(s.id);
                  return (
                    <label
                      key={s.id}
                      className="flex items-center gap-2 text-xs px-3 py-1.5 border-b last:border-b-0 border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                    >
                      <input type="checkbox" checked={checked} onChange={() => toggleStore(s.id)} />
                      <span className="font-medium">{s.name}</span>
                      <span className="text-gray-400">#{s.id}</span>
                    </label>
                  );
                })
              )}
            </div>
          )}

          {mode === 'all' && (
            <div className="text-xs text-gray-600 dark:text-gray-300 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded p-3">
              This will enqueue a scan for every active store ({stores ? stores.length : '…'}{' '}
              total). Results appear in the table within ~5 minutes depending on queue length.
            </div>
          )}

          {rescan.isError && (
            <div className="text-xs text-red-700 bg-red-50 rounded p-2">
              {(rescan.error as Error).message}
            </div>
          )}
          {rescan.isSuccess && rescan.data && (
            <div className="text-xs text-green-700 bg-green-50 rounded p-2">
              Enqueued {rescan.data.enqueuedCount} scan
              {rescan.data.enqueuedCount === 1 ? '' : 's'}.
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-800">
          <button
            onClick={close}
            className="text-xs px-3 py-1.5 rounded border border-gray-200 dark:border-gray-800"
          >
            Close
          </button>
          <button
            onClick={submit}
            disabled={
              rescan.isPending ||
              dateInvalid() !== null ||
              (mode === 'single' && !singleId) ||
              (mode === 'list' && selectedIds.size === 0)
            }
            className="text-xs px-3 py-1.5 rounded bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900 disabled:opacity-50"
          >
            {rescan.isPending
              ? 'Enqueuing…'
              : (() => {
                  const storesLabel =
                    mode === 'all'
                      ? 'all stores'
                      : mode === 'list'
                        ? `${selectedIds.size} store${selectedIds.size === 1 ? '' : 's'}`
                        : '1 store';
                  const days = rangeDayCount();
                  const daysLabel =
                    dateMode === 'yesterday'
                      ? ''
                      : dateMode === 'single'
                        ? ' (1 day)'
                        : ` (${days} day${days === 1 ? '' : 's'})`;
                  return `Run for ${storesLabel}${daysLabel}`;
                })()}
          </button>
        </div>
      </div>
    </div>
  );
}
